import Article from '@/components/article';
import BandeauTop from '@/components/BandeauTop';
import Footer from '@/components/footer';
import { HeroIllustration } from '@/components/hero-illustration';
import { LogoCarousel } from '@/components/logo-carousel';
import ProductsSlider from '@/components/products-slider';
import Top from '@/components/top';
import { locales } from '@/constants/languages';
import { useUrl } from '@/hooks/use-url';
import type { GetStaticPropsContext } from 'next';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import Link from 'next/link';
import { useRouter } from 'next/router';
import Script from 'next/script';
import { useEffect } from 'react';
import Cookies from 'universal-cookie';

const TrustpilotRating = dynamic(() => import('@/components/truspilot-rating').then((mod) => mod.TrustpilotRating), {
  ssr: false,
});

export default function Page({ site, page }) {
  const lang = site.html_lang;

  const router = useRouter();
  const cookies = new Cookies();
  const { getAppUrl } = useUrl();

  useEffect(() => {
    if (router.query.utm_medium) {
      cookies.set('tracking', JSON.stringify(router.query), { path: '/', domain: `.${window.location.hostname}` });
    }
  }, [router.query.utm_medium]);

  return (
    <>
      <Head>
        <meta name="viewport" content="initial-scale=1.0, width=device-width" />
        <link rel="icon" href="/favicon.svg" type="image/svg+xml" />
        {/* SEO */}
        <title>{site.meta_title && site.meta_title}</title>
        <meta name="description" content={site.meta_description} />
        {/*  canonical */}
        <link rel="canonical" href={site.url.includes('makemycv.com/en') ? site.url.replace('/en', '') : site.url} />
        {/* hreflang tags */}
        {[
          { lang: 'fr', url: 'https://makemycv.fr' },
          { lang: 'en', url: 'https://makemycv.com' },
          ...locales
            .filter((lang) => lang !== 'fr' && lang !== 'en')
            .map((lang) => ({ lang, url: `https://makemycv.com/${lang}` })),
        ].map(({ lang, url }) => (
          <link key={lang} rel="alternate" hrefLang={lang} href={url} />
        ))}
        <link rel="alternate" hrefLang="x-default" href="https://makemycv.com" />
        {/* Open graph */}
        {site.content_value_proposal && site.content_value_proposal.image && site.content_value_proposal.image.url && (
          <meta property="og:image" content={`${site.content_value_proposal.image.url}`} />
        )}
        {site.content_value_proposal &&
          site.content_value_proposal.image &&
          site.content_value_proposal.image.width && (
            <meta property="og:image:width" content={site.content_value_proposal.image.width} />
          )}
        {site.content_value_proposal &&
          site.content_value_proposal.image &&
          site.content_value_proposal.image.height && (
            <meta property="og:image:height" content={site.content_value_proposal.image.height} />
          )}
        <meta property="og:title" content={site.meta_title && site.meta_title} />
        <meta
          property="og:url"
          content={site.url.includes('makemycv.com/en') ? site.url.replace('/en', '') : site.url}
        />
        <meta property="og:description" content={site.meta_description} />
        <meta property="og:site_name" content={site.title} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content={process.env.NEXT_PUBLIC_LOCALE} />
        <meta name="p:domain_verify" content="c309a8221aea76f6c5ae1ef4239116fa" />
        {/* JSON-LD structured data */}
        {site.FAQPageJSON && (
          <Script
            id="faq-schema"
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(site.FAQPageJSON),
            }}
          />
        )}
      </Head>
      <div className="page">
        {site.content_top_banner && site.content_top_banner.is_active && <BandeauTop site={site} />}

        <Top site={site} page={page} show_login_only={false} />

        <div>
          {site.content_value_proposal && (
            <section className="m-auto max-w-[1200px] px-4 py-8 md:pt-2 xl:p-0">
              <div className="relative flex flex-col items-center gap-4 overflow-hidden rounded-2xl bg-[linear-gradient(to_bottom,_#ECF1FD_0%,_#ECF1FD_50%,_rgba(236,241,253,0)_100%)] px-4 md:px-10 md:pt-6 lg:flex-row lg:pb-14 xl:px-16 xl:py-10">
                <div className="flex flex-col items-center lg:w-1/2 lg:items-start">
                  <div className="flex items-center gap-2 pb-4 pt-6">
                    <img src="/images/home/customers-pictures.webp" alt="customers" width={60} height={24} />
                    <div className="text-sm md:text-base">
                      <span className="font-bold text-indigo-600">
                        {site.content_customers_count.one_million ? site.content_customers_count.one_million : ''}
                      </span>
                      {site.content_customers_count.customers_count ? site.content_customers_count.customers_count : ''}
                    </div>
                  </div>

                  <div className="flex flex-col gap-4 text-center md:gap-2.5 lg:gap-4 lg:text-left">
                    <div className="font-[Spoof] text-[28px] font-semibold leading-tight text-[#061231] subpixel-antialiased md:text-[44px] xl:mr-10">
                      {site.content_value_proposal.title}
                    </div>
                    <div className="text-sm text-[#596073] md:text-[18px] md:leading-normal lg:mr-10">
                      {site.content_value_proposal.subtitle}
                    </div>
                  </div>

                  <div className="flex w-full justify-center gap-4 py-6 lg:justify-start lg:py-8">
                    <Link href={getAppUrl(`/create/resume?ref=/`)}>
                      <button
                        type="button"
                        className="plausible-event-name=MainCTA relative flex h-[54px] min-w-[180px] animate-ctaPulse items-center justify-center rounded-md bg-[#4B4FF2] px-6 text-base font-medium text-white hover:cursor-pointer md:h-[64px] md:text-lg"
                      >
                        {site.content_value_proposal.cta}
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="23"
                          height="22"
                          fill="none"
                          viewBox="0 0 23 22"
                          className="absolute left-[-20px] top-[-19px]"
                        >
                          <path
                            stroke="#0E2053"
                            strokeLinecap="round"
                            strokeWidth="2"
                            d="M15.567 1.5c1.046 2.36 3.227 5.686 3.578 10.18M1 3.529c3.327 1.909 10.729 6.937 13.723 11.779M12.037 19.346c-2.136-.538-6.764-1.921-8.19-3.145"
                          ></path>
                        </svg>
                      </button>
                    </Link>
                    <div className="hidden items-center lg:flex">
                      <div className="relative">
                        <svg xmlns="http://www.w3.org/2000/svg" width="53" height="41" fill="none" viewBox="0 0 53 41">
                          <path
                            stroke="#ECF1FD"
                            strokeLinecap="round"
                            strokeWidth="2"
                            d="M43.516 3.514C33.916.3 12.434-1.426 3.296 17.37c-11.42 23.496 22.795 24.118 35.82 20.483C46.91 35.68 52 30.36 52 22.661c0-6.627-8.84-16.587-24.838-14.704"
                          ></path>
                        </svg>
                        <div className="absolute left-0 top-0 ml-[2px] flex h-full w-full items-center justify-center text-[22px] font-bold text-indigo-600">
                          2x
                        </div>
                      </div>
                      <div className="font-medium text-[#61687B]">{site.content_value_proposal.many_interviews}</div>
                    </div>
                  </div>

                  <TrustpilotRating />
                </div>

                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="387"
                  height="429"
                  fill="none"
                  viewBox="0 0 387 429"
                  className="absolute right-0 top-0 hidden lg:block"
                >
                  <path
                    fill="#0E2053"
                    d="M415.578-171H45.218L1.334 183.392c-11.022 89.211 47.49 171.931 130.061 183.9l418.007 60.378c5.936.851 11.767 1.224 17.598 1.33V-7.368C567-97.379 498.842-171 415.578-171z"
                  ></path>
                </svg>

                <HeroIllustration type="CV" site={site} />
              </div>

              <div className="relative flex min-h-[100px] flex-col items-center gap-4 rounded-2xl bg-[#F6F7FA] py-4 md:flex-row md:justify-center">
                <div className="text-sm font-medium md:pl-6 lg:text-base xl:pl-0">{site.content_hired_by}</div>

                <LogoCarousel lang={lang} />

                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="34"
                  height="26"
                  fill="none"
                  viewBox="0 0 34 26"
                  className="absolute left-[0] top-[0]"
                >
                  <path
                    fill="#4B4FF2"
                    d="M26.292 19.52c.157.323.265.666.323 1.02.086.49.117 1.031.187 1.638.023.247.06.493.11.736 0 .066.034.136.061.217.028.086.062.169.102.25.048.099.107.193.175.28.245.294.608.465.992.467.234-.002.463-.067.661-.191.259-.178.461-.427.584-.716.154-.361.256-.742.304-1.132l.035-.261v-.362-.475a2.236 2.236 0 010-.225c0-.187.182-.331.315-.214.118.114.208.256.26.412.064.183.115.37.152.56l.031.117v.063l.05.136c.063.167.13.334.192.506.038.093.128.105.256.124a.619.619 0 00.471-.16c.17-.155.303-.347.39-.56.06-.109.108-.225.14-.346a3.26 3.26 0 00-.055-.658 7.646 7.646 0 00-.237-.957 3.01 3.01 0 00-.39-.778 2.584 2.584 0 00-.447-.526 2.012 2.012 0 00-.455-.295 1.558 1.558 0 00-.502-.129 1.397 1.397 0 00-.603.078 2.149 2.149 0 00-.269.133c-.06.044-.117.092-.171.143-.104.111-.197.232-.276.362a3.019 3.019 0 00-.16.39c-.05.177-.082.36-.093.544-.04.39-.04.837-.055 1.226v.338l-.02.277c0 .116-.019.23-.034.342-.027.202-.076.4-.148.592a.48.48 0 01-.078.147c-.023 0 .039.032.074.074l.054.07.032.04.023.046v-.113c0-.023-.043.203-.02.105 0 0 0 .031 0 0 0-.03 0 0 .043-.05l.047-.043h.023l.043-.031a.39.39 0 01.113-.07.316.316 0 010-.059 3.355 3.355 0 01-.105-.65l-.062-.824a7.595 7.595 0 00-.304-1.806 2.335 2.335 0 00-.829-1.117 1.826 1.826 0 00-.68-.276 1.634 1.634 0 00-.752 0 1.84 1.84 0 00-.906.556c-.204.225-.38.475-.522.744-.256.487-.482.99-.677 1.506a22.635 22.635 0 01-1.03 2.183c-.079.151-.176.287-.25.424-.132.26-.198.416-.16.556.083.188.199.36.343.506.078.093.144.202.218.3.122.167.33.25.533.21a.993.993 0 00.654-.397 7.978 7.978 0 00.992-2.129c.23-.7.412-1.4.638-2.008.077-.204.166-.404.265-.599.061-.114.134-.223.218-.323.03-.03 0-.05.05 0s.074.063.105.117l.031-.055z"
                  ></path>
                  <path
                    fill="#4B4FF2"
                    d="M8.027 12.065l-.868-.6-.84-.633c-.288-.203-.549-.429-.821-.65a12.122 12.122 0 01-.778-.677 12.669 12.669 0 01-1.382-1.413 4.354 4.354 0 01-.828-1.475 2.175 2.175 0 01-.043-1.019c.084-.336.236-.651.447-.926a5.447 5.447 0 011.79-1.436c.783-.405 1.61-.72 2.463-.938a22.895 22.895 0 015.495-.68c1.67.002 3.338.106 4.996.31 2.124.254 4.253.604 6.362 1.04.657.124.587.038.062-.203-.26-.12-.638-.28-1.097-.467-.46-.187-1.004-.389-1.603-.564l-1.604-.467c-.537-.14-1.077-.288-1.622-.42A26.916 26.916 0 0014.82.232a25.266 25.266 0 00-6.833 0 19.222 19.222 0 00-4.19 1.05c-.735.292-1.42.694-2.032 1.195A4.918 4.918 0 00.253 4.571a4.67 4.67 0 00-.047 2.887c.264.847.68 1.638 1.23 2.335a21.872 21.872 0 003.381 3.288 35.307 35.307 0 004.716 3.136 40.898 40.898 0 007.856 3.311c.518.195 1.047.327 1.557.49.51.164 1.039.34 1.556.471 1.055.25 2.105.576 3.195.728.475.082.938.187 1.424.253.378.05.76.062 1.14.035.264-.007.525-.048.778-.12.375-.1.688-.36.856-.709.097-.156.18-.32.25-.49.163-.553-.537-1.05-1.755-1.3-.907-.151-1.81-.389-2.724-.548-.46-.074-.91-.195-1.366-.308l-1.366-.315a47.516 47.516 0 01-5.397-1.732 35.352 35.352 0 01-7.467-3.965l-.043.047z"
                  ></path>
                  <path
                    fill="#4B4FF2"
                    d="M25.872 25.684a24.928 24.928 0 004.381-1.257 8.477 8.477 0 001.611-.778 3.412 3.412 0 001.288-1.467c.15-.37.192-.775.121-1.167a2.453 2.453 0 00-.428-.989 8 8 0 00-.514-.61c-.167-.183-.35-.355-.521-.53a15.03 15.03 0 00-1.117-.957c-.673-.537-1.362-1.02-1.996-1.521-.821-.646-1.525-1.331-2.335-1.946-.249-.19-.529.082-.638.553a1.81 1.81 0 00.436 1.638 25.728 25.728 0 002.521 2.128 26.04 26.04 0 012.335 1.884c.167.139.31.303.428.486.05.093.05.206 0 .3-.086.157-.21.291-.362.389-.219.16-.452.299-.696.416a16.477 16.477 0 01-3.891 1.28c-.425.09-.857.171-1.285.245l-.65.102-.408.058c-.175 0-.362.043-.553.047-.124.03-.143-.094-.143-.176a1.608 1.608 0 010-.272c.026-.195.065-.39.116-.58.086-.315.183-.634.288-.95.327-.976.779-1.894 1.207-2.84.093-.21.202-.389.284-.622.307-.798.268-1.117-.187-1.456a2.395 2.395 0 00-.312-.221c-.389-.191-.778 0-1.105.556a19.98 19.98 0 00-1.276 2.568c-.187.452-.37.915-.525 1.393-.078.238-.152.483-.222.732l-.113.408c-.058.234-.12.467-.163.705-.148.7-.207 1.408.035 1.88l.081.182c.033.06.07.117.113.171l.035.04v.022c0-.019-.039.04.047-.038l.074.062.136.113c.088.09.189.166.3.225.142.052.292.083.443.09.347.027.696.027 1.043 0l.537-.055.424-.05a31 31 0 001.129-.172l.027-.019z"
                  ></path>
                </svg>
              </div>
            </section>
          )}

          {site.content_create_steps && (
            <section className="m-auto max-w-[1200px] px-4 py-5 lg:pb-16 lg:pt-8 xl:pb-24 xl:pt-20">
              <div className="flex flex-col items-center gap-6 lg:gap-12">
                <div className="text-center font-[Spoof] text-2xl font-bold leading-tight subpixel-antialiased lg:text-[38px]">
                  {site.content_create_steps.title}
                </div>

                <div className="flex flex-col gap-8 md:flex-row">
                  {site.content_create_steps.list &&
                    site.content_create_steps.list.map((step, index) => {
                      return (
                        <div key={index} className="flex max-w-[276px] flex-col items-center gap-4 md:w-1/4">
                          <img src={`/images/home/create-step-${index + 1}.webp`} width={276} height={250} />
                          <div className="w-full text-center font-[Spoof] text-lg font-semibold subpixel-antialiased lg:text-xl lg:font-medium">
                            {step.title}
                          </div>
                        </div>
                      );
                    })}
                </div>

                <div className="mt-2 flex w-full flex-col items-center border-t border-[#E6EAF2] py-10 lg:mt-0 lg:border-0 lg:py-6">
                  <Link href={getAppUrl(`/create/resume?ref=/`)}>
                    <div className="relative flex h-[58px] animate-ctaPulse items-center justify-center rounded-md bg-[#4B4FF2] px-6 text-lg font-medium text-white hover:cursor-pointer lg:h-[64px]">
                      {site.content_create_steps.cta_button}
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="157"
                        height="26"
                        fill="none"
                        viewBox="0 0 157 26"
                        className="absolute bottom-[-40px] left-[-78px] hidden lg:block"
                      >
                        <path
                          stroke="#ECF1FD"
                          strokeLinecap="round"
                          strokeWidth="4"
                          d="M2 7.613C17.86 4.68 64.922-.31 126.294 3.211M36.376 16.21C47.648 12.89 81.38 5.679 126.134 3.396M36.748 16.15c15.248-1.605 60.217-2.838 118.112 5.075"
                        ></path>
                      </svg>
                    </div>
                  </Link>
                </div>
              </div>
            </section>
          )}

          {site.content_products_resumes && (
            <section className="products-resumes m-auto max-w-[1200px] lg:px-5 xl:px-0">
              <div className="relative overflow-hidden bg-[linear-gradient(240deg,_#0E2053_60%,_#4B4FF2_153%)] py-6 text-white lg:rounded-2xl lg:bg-[linear-gradient(240deg,_#0E2053_60.46%,_#4B4FF2_152.85%)] lg:py-16">
                <div className="flex flex-col gap-4 px-5 text-center md:gap-1 md:pl-10 lg:max-w-[50%] lg:gap-2 lg:pl-20 lg:text-left">
                  <p className="font-[Spoof] text-[28px] font-semibold leading-tight subpixel-antialiased lg:text-[38px]">
                    {site.content_products_resumes.title}
                  </p>
                  <p className="px-1 text-lg">{site.content_products_resumes.subtitle}</p>
                </div>

                <div className="relative z-10">
                  <ProductsSlider site={site} type="CV" tracking_ref="/" />
                </div>

                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="469"
                  height="751"
                  fill="none"
                  viewBox="0 0 469 751"
                  className="absolute bottom-0 right-0 hidden lg:block"
                >
                  <path
                    fill="#4B4FF2"
                    d="M1002 709.488V77.198L397.17 2.276C244.917-16.539 103.742 83.355 83.315 224.321L-19.73 937.957C-21.183 948.09-21.818 958.045-22 968h744.735C876.354 968 1002 851.638 1002 709.488z"
                  ></path>
                </svg>

                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="339"
                  height="434"
                  fill="none"
                  viewBox="0 0 339 434"
                  className="absolute bottom-0 left-0 lg:hidden"
                >
                  <path
                    fill="#4B4FF2"
                    d="M-412 520.389V56.622L31.581 1.67c111.662-13.8 215.2 59.468 230.181 162.863l75.573 523.431c1.066 7.433 1.532 14.734 1.665 22.036h-546.187C-319.851 710-412 624.652-412 520.389z"
                  ></path>
                </svg>
              </div>
            </section>
          )}

          {site.content_features && (
            <section className="m-auto max-w-[1200px] px-4 py-8 lg:p-0 lg:pt-20">
              <div className="">
                <div className="mb-8 w-full text-center font-[Spoof] text-[28px] font-semibold leading-tight subpixel-antialiased lg:mb-12 lg:text-[38px]">
                  {site.content_features.title}
                </div>

                <div className="flex flex-col items-center gap-6 md:flex-row md:flex-wrap md:items-start md:justify-center">
                  {site.content_features.list.map((feature, index) => {
                    return (
                      <div
                        key={feature.title}
                        className="flex max-w-[276px] shrink-0 flex-col items-center rounded-2xl bg-[#F6F7FA] p-4 md:min-h-[250px] md:w-[calc(50%_-_24px)] md:max-w-none lg:min-h-[300px] lg:w-[calc(33%_-_30px)] lg:p-6 xl:w-[calc(33%_-_12px)]"
                      >
                        <div className="mb-4">
                          {feature.image && feature.image.url && (
                            <img
                              alt={feature.title}
                              src={`/images/home/features/${index + 1}.png`}
                              width="66"
                              height="62"
                              loading="lazy"
                            />
                          )}
                        </div>
                        <div className="flex flex-col items-center gap-4 text-center">
                          <p className="font-[Spoof] text-lg font-semibold leading-tight subpixel-antialiased lg:text-[22px] lg:font-medium">
                            {feature.title}
                          </p>
                          <p className="text-base text-[#61687B]">{feature.description}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>
          )}

          <main className="main-content home-content">
            {page && (
              <Article
                site={site}
                page={page}
                showTitleInContent={true}
                showAuthor={false}
                showHeader={false}
                showHeadersNav={false}
                showBreadcrumb={false}
                showSummary={false}
                showFooter={false}
                showDateUpdate={false}
              />
            )}
          </main>

          {site.content_value_proposal_footer && (
            <section className="value-proposal-footer">
              <div className="section-inner">
                <div className="content-inner">
                  <p className="title">{site.content_value_proposal_footer.title}</p>
                  <p className="subtitle">{site.content_value_proposal_footer.subtitle}</p>
                  <Link href={getAppUrl(`/create/resume?ref=/`)}>
                    <button className="cta">
                      <span className="cta-text">{site.content_value_proposal_footer.cta}</span>
                    </button>
                  </Link>
                </div>
                <div className="image">
                  {site.content_value_proposal_footer.image &&
                    site.content_value_proposal_footer.image.url &&
                    site.content_value_proposal_footer.image.width &&
                    site.content_value_proposal_footer.image.height && (
                      <img
                        alt={site.content_value_proposal_footer.image.alt}
                        src={site.content_value_proposal_footer.image.url}
                        width={site.content_value_proposal_footer.image.width / 2}
                        height={site.content_value_proposal_footer.image.height / 2}
                        loading="lazy"
                      />
                    )}
                </div>
              </div>
            </section>
          )}

          <Footer site={site} footer_legals_brands={site.footer_legals_brands ? site.footer_legals_brands : null} />
        </div>
      </div>
    </>
  );
}

export async function getStaticProps({ locale }: GetStaticPropsContext) {
  const response = await fetch(`${process.env.BACK_HOST}/api/fetch/sites/findOneByLocale?locale=${locale}`, {
    headers: { authorization: `Bearer ${process.env.BACK_API_KEY}` },
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(`Failed to fetch site, received status ${response.status}`);
  }

  const messages = (await import(`@/i18n/locales/${locale}/messages.json`)).default;

  return {
    props: {
      site: data.site,
      page: data.page,
      messages,
    },
    revalidate: 3600,
  };
}
